import { ArrowLeft, CheckCircle } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { AlertFloating } from '@/lib/base/overlays';

import type { CreateMergeFieldBody } from '../api/fieldMappings';
import { useMailchimpMappings } from '../hooks/useMailchimpMappings';
import type { MailchimpFieldMapping } from '../types';
import type { MappingUpdate } from './EditableMappingRow';
import { type AhoyPropertyOption, MappingSections } from './MappingSections';

interface FieldMappingManagerProps {
  orgId: number | string;
  availableAhoyProperties: AhoyPropertyOption[];
  onBack: () => void;
}

const EMPTY_MAPPINGS: MailchimpFieldMapping[] = [];

function isSyncing(m: MailchimpFieldMapping): boolean {
  return (
    !!m.ahoy_property &&
    m.sync_direction !== 'skipped' &&
    m.sync_direction !== ''
  );
}

export function FieldMappingManager({
  orgId,
  availableAhoyProperties,
  onBack,
}: FieldMappingManagerProps) {
  const { mappings, isLoading, error, updateMapping, createMergeField } =
    useMailchimpMappings(orgId);

  const allMappings = useMemo(
    () => mappings.data?.mappings ?? EMPTY_MAPPINGS,
    [mappings.data]
  );

  const syncingCount = useMemo(
    () => allMappings.filter(isSyncing).length,
    [allMappings]
  );

  const handleUpdate = useCallback(
    (mappingId: number, body: MappingUpdate) => {
      updateMapping.mutate({ mappingId, body });
    },
    [updateMapping]
  );

  const handleCreateMergeField = useCallback(
    (body: CreateMergeFieldBody) => {
      createMergeField.mutate(body);
    },
    [createMergeField]
  );

  const savingId =
    updateMapping.isPending && updateMapping.variables
      ? updateMapping.variables.mappingId
      : null;
  const creatingProp =
    createMergeField.isPending && createMergeField.variables
      ? createMergeField.variables.ahoy_property
      : null;

  const isMutating = updateMapping.isPending || createMergeField.isPending;
  const isMutateSuccess = updateMapping.isSuccess || createMergeField.isSuccess;
  let saveStatus = null;
  if (isMutating) {
    saveStatus = (
      <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-secondary)]'>
        <LoadingIndicator size='xs' inline />
        Saving…
      </span>
    );
  } else if (isMutateSuccess) {
    saveStatus = (
      <span className='flex items-center gap-1.5 text-body-small text-[var(--color-text-success-primary)]'>
        <CheckCircle className='h-4 w-4' aria-hidden='true' />
        All changes saved
      </span>
    );
  }

  return (
    <div className='space-y-5'>
      <div className='flex items-start justify-between gap-4'>
        <div>
          <h3 className='text-heading-3 text-[var(--color-text-primary)]'>
            Field mappings
          </h3>
          <p className='mt-0.5 text-body-small text-[var(--color-text-secondary)]'>
            Changes save automatically and apply to future syncs.
          </p>
        </div>
        <Button
          color='secondary'
          size='sm'
          onClick={onBack}
          iconLeading={ArrowLeft}
        >
          Back
        </Button>
      </div>

      {!isLoading && !error && allMappings.length > 0 && (
        <div className='flex items-center justify-between gap-3'>
          <p className='text-body-small text-[var(--color-text-secondary)]'>
            <span className='font-semibold text-[var(--color-text-primary)]'>
              {syncingCount}
            </span>{' '}
            of {allMappings.length} fields syncing
          </p>
          <div className='min-h-[20px]'>{saveStatus}</div>
        </div>
      )}

      {(updateMapping.isError || createMergeField.isError) && (
        <AlertFloating
          color='error'
          title='Couldn’t save your last change'
          description='Please try again.'
        />
      )}

      {isLoading && (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          Loading field mappings…
        </p>
      )}

      {error && (
        <AlertFloating
          color='error'
          title="We couldn't load field mappings"
          description='Please refresh and try again.'
        />
      )}

      {!isLoading && !error && allMappings.length > 0 && (
        <MappingSections
          mappings={allMappings}
          availableAhoyProperties={availableAhoyProperties}
          savingId={savingId}
          creatingProp={creatingProp}
          onUpdate={handleUpdate}
          onCreateMergeField={handleCreateMergeField}
        />
      )}

      {!isLoading && !error && allMappings.length === 0 && (
        <p className='text-sm text-[var(--color-text-secondary)]'>
          No field mappings found for this integration.
        </p>
      )}
    </div>
  );
}
